import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import MirakalousLogo from '../../components/MirakalousLogo';
import CaseStudyCard from './components/CaseStudyCard';
import CaseStudyModal from './components/CaseStudyModal';
import SimilarChallengesSection from './components/SimilarChallengesSection';
import IndustryShowcase from './components/IndustryShowcase';
import ProjectCard from './components/ProjectCard';
import ProjectModal from './components/ProjectModal';

const ClientSuccessStoriesPage = () => {
  const [selectedCaseStudy, setSelectedCaseStudy] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [filteredCaseStudies, setFilteredCaseStudies] = useState([]);
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');
  const [activeTab, setActiveTab] = useState('companies'); // companies | projects

  // Mock data for case studies
  const caseStudies = [
    {
      id: 1,
      company: "Ford Motor Company",
      industry: "Automotive",
      title: "Code Conversion Tools",
      description: "Built automated code transformation and modernization tools to enable Ford's software development teams to migrate legacy codebases efficiently.",
      challenge: `Ford Motor Company needed to modernize their legacy software systems across multiple platforms. The existing codebase contained millions of lines of code in outdated languages and frameworks that were becoming increasingly difficult to maintain and scale. Manual code conversion was time-consuming, error-prone, and costly.`,
      solutionDescription: `We developed intelligent code conversion tools using advanced AI and machine learning techniques. The solution analyzes legacy code patterns, understands business logic, and automatically converts code to modern languages and frameworks while maintaining functionality and improving performance.`,
      solutionType: "Code Conversion",
      companyLogo: "/assets/images/Ford-logo.jpg",
      keyMetrics: [
        { value: "75%", label: "Code Conversion Accuracy" },
        { value: "60%", label: "Development Time Saved" },
        { value: "$1.2M", label: "Cost Savings" }
      ],
      technologies: ["Python", "AST Parsing", "Machine Learning", "Docker", "Kubernetes", "CI/CD"],
      timeline: "8 months",
      teamSize: "6 engineers",
      investment: "$650K",
      roi: "280% in 12 months",
      testimonial: {
        name: "Sarah Mitchell",
        title: "VP of Software Engineering",
        avatar: "https://randomuser.me/api/portraits/women/32.jpg",
        quote: "The code conversion tools revolutionized our modernization efforts.",
        fullQuote: "The code conversion tools revolutionized our modernization efforts. We were able to migrate our legacy systems faster than we ever imagined possible, with minimal errors and maximum efficiency."
      },
      achievements: [
        { value: "2M+", label: "Lines of Code Converted" },
        { value: "95%", label: "Functional Accuracy" },
        { value: "Zero", label: "Critical Bugs" }
      ],
      additionalTestimonials: [
        {
          name: "Mike Johnson",
          title: "Lead Developer",
          avatar: "https://randomuser.me/api/portraits/men/45.jpg",
          quote: "Automated conversion saved us months of manual work."
        }
      ],
      shortDescription: "Code conversion tools enabling automated legacy system modernization",
      similarityScore: 95
    },
    {
      id: 2,
      company: "Siemens Technology",
      industry: "Technology",
      title: "Secure On-Prem MCP Compliant Agent",
      description: "Built a secure, on-premises Model Context Protocol (MCP) compliant AI agent for code generation with enterprise-grade security and compliance.",
      challenge: `Siemens Technology required an AI-powered code generation solution that could operate within their highly secure on-premises environment while maintaining strict compliance with industry regulations. The solution needed to handle sensitive proprietary code without data leaving their infrastructure.`,
      solutionDescription: `We developed a secure on-premises AI agent that complies with Model Context Protocol standards. The solution provides intelligent code generation capabilities while ensuring all data processing occurs within Siemens' secure infrastructure, maintaining complete data sovereignty and regulatory compliance.`,
      solutionType: "Secure AI Agent",
      companyLogo: "/assets/images/Siemens-logo.png",
      keyMetrics: [
        { value: "100%", label: "On-Prem Deployment" },
        { value: "85%", label: "Code Generation Accuracy" },
        { value: "Zero", label: "Data Breaches" }
      ],
      technologies: ["Python", "FastAPI", "Docker", "Kubernetes", "Redis", "PostgreSQL", "Security Frameworks"],
      timeline: "10 months",
      teamSize: "8 engineers",
      investment: "$950K",
      roi: "220% in 18 months",
      testimonial: {
        name: "Dr. Klaus Weber",
        title: "Head of Secure Computing",
        avatar: "https://randomuser.me/api/portraits/men/52.jpg",
        quote: "This secure AI agent exceeded our compliance and security requirements.",
        fullQuote: "This secure AI agent exceeded our compliance and security requirements. The on-premises deployment ensures our proprietary code never leaves our infrastructure while providing powerful AI-assisted development capabilities."
      },
      achievements: [
        { value: "100%", label: "MCP Compliance" },
        { value: "24/7", label: "Secure Operation" },
        { value: "Enterprise", label: "Security Grade" }
      ],
      shortDescription: "Secure on-premises AI agent for compliant code generation",
      similarityScore: 88
    },
    {
      id: 3,
      company: "Citibank",
      industry: "Financial Services",
      title: "AI Tools Development",
      description: "Developed custom AI tools for financial services to enhance operational efficiency, risk management, and customer experience across multiple banking divisions.",
      challenge: `Citibank required specialized AI tools to handle complex financial operations, regulatory compliance, and customer service automation. The solutions needed to integrate with existing banking systems while maintaining the highest security standards and regulatory compliance.`,
      solutionDescription: `We built a suite of custom AI tools including intelligent document processing, automated risk assessment, customer service chatbots, and predictive analytics platforms. All solutions were designed with banking-grade security and compliance requirements in mind.`,
      solutionType: "AI Tools",
      companyLogo: "/assets/images/Citibank-logo.png",
      keyMetrics: [
        { value: "70%", label: "Process Automation" },
        { value: "50%", label: "Operational Efficiency" },
        { value: "$8M", label: "Annual Savings" }
      ],
      technologies: ["Python", "TensorFlow", "FastAPI", "PostgreSQL", "Redis", "Docker", "Security Frameworks"],
      timeline: "12 months",
      teamSize: "10 engineers",
      investment: "$1.8M",
      roi: "320% in 18 months",
      testimonial: {
        name: "Jennifer Chen",
        title: "Chief Technology Officer",
        avatar: "https://randomuser.me/api/portraits/women/41.jpg",
        quote: "The AI tools have transformed our operational capabilities.",
        fullQuote: "The AI tools have transformed our operational capabilities. We've seen significant improvements in efficiency and customer satisfaction while maintaining the strict security and compliance standards required in financial services."
      },
      achievements: [
        { value: "100%", label: "Compliance Rate" },
        { value: "99.9%", label: "System Reliability" },
        { value: "24/7", label: "Operation Coverage" }
      ],
      shortDescription: "Custom AI tools enhancing financial services operations",
      similarityScore: 82
    },
    {
      id: 4,
      company: "Point32Health",
      industry: "Healthcare",
      title: "AI Gateways & Custom Library",
      description: "Built AI gateways and custom AI-enabled libraries to enhance healthcare infrastructure and enable intelligent healthcare applications.",
      challenge: `Point32Health required robust AI infrastructure to support various healthcare applications while maintaining HIPAA compliance and ensuring secure data handling. They needed scalable AI gateways and custom libraries that could integrate with existing healthcare systems.`,
      solutionDescription: `We developed comprehensive AI gateways that serve as secure entry points for AI services and created custom AI-enabled libraries for healthcare-specific use cases. The solution includes intelligent routing, load balancing, security controls, and healthcare-compliant data processing capabilities.`,
      solutionType: "AI Infrastructure",
      companyLogo: "/assets/images/Point32Health-logo.png",
      keyMetrics: [
        { value: "99.9%", label: "Gateway Uptime" },
        { value: "80%", label: "Performance Improvement" },
        { value: "100%", label: "HIPAA Compliance" }
      ],
      technologies: ["Python", "FastAPI", "Kubernetes", "Docker", "PostgreSQL", "Redis", "Healthcare APIs"],
      timeline: "10 months",
      teamSize: "8 engineers",
      investment: "$1.4M",
      roi: "250% in 18 months",
      testimonial: {
        name: "Dr. Maria Rodriguez",
        title: "Chief Technology Officer",
        avatar: "https://randomuser.me/api/portraits/women/38.jpg",
        quote: "The AI gateways and custom libraries transformed our healthcare infrastructure.",
        fullQuote: "The AI gateways and custom libraries transformed our healthcare infrastructure. We now have a scalable, secure foundation for all our AI-powered healthcare applications with seamless integration capabilities."
      },
      achievements: [
        { value: "24/7", label: "Service Availability" },
        { value: "<50ms", label: "Response Latency" },
        { value: "Enterprise", label: "Security Grade" }
      ],
      shortDescription: "AI gateways and custom libraries for healthcare infrastructure",
      similarityScore: 75
    },
    {
      id: 5,
      company: "DIEXTRA App",
      industry: "Healthcare Technology",
      title: "AI-Powered Healthcare Application",
      description: "Developed an AI-powered healthcare application with intelligent features for patient management, diagnosis assistance, and treatment optimization.",
      challenge: `DIEXTRA App needed to create a comprehensive healthcare application that could assist medical professionals with patient management while ensuring data privacy and regulatory compliance. The app required intelligent features that could adapt to various medical scenarios and provide actionable insights.`,
      solutionDescription: `We built an AI-powered healthcare application with advanced machine learning capabilities for patient data analysis, predictive health insights, and intelligent recommendations. The solution includes secure data handling, real-time analytics, and seamless integration with healthcare systems.`,
      solutionType: "Healthcare AI",
      companyLogo: "/assets/images/dietra_logo.png",
      keyMetrics: [
        { value: "90%", label: "Diagnostic Accuracy" },
        { value: "65%", label: "Efficiency Improvement" },
        { value: "100%", label: "HIPAA Compliance" }
      ],
      technologies: ["React Native", "Python", "TensorFlow", "FastAPI", "PostgreSQL", "AWS", "Healthcare APIs"],
      timeline: "14 months",
      teamSize: "12 engineers",
      investment: "$2.2M",
      roi: "280% in 24 months",
      testimonial: {
        name: "Dr. Alex Thompson",
        title: "Chief Medical Officer",
        avatar: "https://randomuser.me/api/portraits/men/29.jpg",
        quote: "DIEXTRA App has revolutionized how we approach patient care.",
        fullQuote: "DIEXTRA App has revolutionized how we approach patient care. The AI-powered insights help us make better decisions faster, and the user experience is exceptional for both medical professionals and patients."
      },
      achievements: [
        { value: "50K+", label: "Active Users" },
        { value: "99.9%", label: "App Uptime" },
        { value: "4.8/5", label: "User Rating" }
      ],
      shortDescription: "AI-powered healthcare app improving diagnostic accuracy by 90%",
      similarityScore: 91
    },
    {
      id: 6,
      company: "HCL",
      industry: "Technology",
      title: "Enterprise AI Engineering Support",
      description: "Supported large-scale AI engineering programs with platform components, evaluation tooling, and integrations across enterprise environments.",
      challenge: `HCL needed additional AI engineering capacity and reusable platform components that could plug into multiple customer programs with strong security and reliability guarantees.`,
      solutionDescription: `We delivered enterprise-ready AI components, evaluation harnesses, and integration adapters, accelerating multiple program timelines while maintaining rigorous quality and compliance standards.`,
      solutionType: "AI Engineering",
      companyLogo: "/assets/images/HCL-logo.png",
      keyMetrics: [
        { value: "30%", label: "Faster Program Delivery" },
        { value: "99.9%", label: "Service Reliability" },
        { value: "12+", label: "Reusable Components" }
      ],
      technologies: ["Python", "FastAPI", "Kubernetes", "Docker", "Redis", "PostgreSQL"],
      timeline: "6 months",
      teamSize: "5 engineers",
      investment: "$520K",
      roi: "210% in 12 months",
      testimonial: {
        name: "Program Director",
        title: "HCL",
        avatar: "https://randomuser.me/api/portraits/men/64.jpg",
        quote: "Their AI platform components helped us move much faster across client programs.",
        fullQuote: "Their AI platform components helped us move much faster across client programs while meeting enterprise-grade security and reliability standards."
      },
      achievements: [
        { value: "5", label: "Programs Accelerated" },
        { value: "Zero", label: "Critical Incidents" },
        { value: "24/7", label: "Support Coverage" }
      ],
      shortDescription: "Accelerated enterprise AI initiatives with reusable components",
      similarityScore: 78
    },
    {
      id: 7,
      company: "Goodleaf",
      industry: "Technology",
      title: "Data & AI Enablement",
      description: "Enabled analytics-ready data pipelines and AI workflows to support decision-making and automation.",
      challenge: `The organization needed reliable data ingestion and governance with a lightweight AI layer to operationalize insights across teams.`,
      solutionDescription: `We designed secure data pipelines, feature stores, and an AI enablement layer with evaluation and monitoring, allowing rapid experimentation and safe rollout of AI use cases.`,
      solutionType: "Data & AI Enablement",
      companyLogo: "/assets/images/GoodLeaf-logo.webp",
      keyMetrics: [
        { value: "4x", label: "Experiment Velocity" },
        { value: "<60ms", label: "Serving Latency" },
        { value: "100%", label: "Governance Coverage" }
      ],
      technologies: ["Python", "Airflow", "dbt", "FastAPI", "Docker"],
      timeline: "5 months",
      teamSize: "4 engineers",
      investment: "$380K",
      roi: "190% in 12 months",
      testimonial: {
        name: "Head of Data",
        title: "Goodleaf",
        avatar: "https://randomuser.me/api/portraits/women/65.jpg",
        quote: "A pragmatic foundation for dependable AI in production.",
        fullQuote: "They helped us stand up a pragmatic, dependable AI foundation—from data pipelines to model serving—so teams could safely deploy use cases to production."
      },
      achievements: [
        { value: "99.9%", label: "Pipeline Uptime" },
        { value: "SOC2", label: "Controls Aligned" },
        { value: "3", label: "Production Use Cases" }
      ],
      shortDescription: "Launched secure data and AI workflows for rapid value",
      similarityScore: 76
    },
    {
      id: 8,
      company: "Norterra",
      industry: "Technology",
      title: "Data & AI Enablement",
      description: "Enabled analytics-ready data pipelines and AI workflows to support decision-making and automation.",
      challenge: `The organization needed reliable data ingestion and governance with a lightweight AI layer to operationalize insights across teams.`,
      solutionDescription: `We designed secure data pipelines, feature stores, and an AI enablement layer with evaluation and monitoring, allowing rapid experimentation and safe rollout of AI use cases.`,
      solutionType: "Data & AI Enablement",
      companyLogo: "/assets/images/Norterra-logo.png",
      keyMetrics: [
        { value: "4x", label: "Experiment Velocity" },
        { value: "<60ms", label: "Serving Latency" },
        { value: "100%", label: "Governance Coverage" }
      ],
      technologies: ["Python", "Airflow", "dbt", "FastAPI", "Docker"],
      timeline: "5 months",
      teamSize: "4 engineers",
      investment: "$380K",
      roi: "190% in 12 months",
      testimonial: {
        name: "Head of Data",
        title: "Norterra",
        avatar: "https://randomuser.me/api/portraits/men/65.jpg",
        quote: "A pragmatic foundation for dependable AI in production.",
        fullQuote: "They helped us stand up a pragmatic, dependable AI foundation—from data pipelines to model serving—so teams could safely deploy use cases to production."
      },
      achievements: [
        { value: "99.9%", label: "Pipeline Uptime" },
        { value: "SOC2", label: "Controls Aligned" },
        { value: "3", label: "Production Use Cases" }
      ],
      shortDescription: "Launched secure data and AI workflows for rapid value",
      similarityScore: 75
    }
  ];

  // Projects portfolio (anonymized clients unless provided)
  const projects = [
    {
      id: 'p1',
      title: 'On-Prem Code Generator trained on 2B LOC',
      duration: '~6 months',
      category: 'Dev Productivity',
      icon: 'Code2',
      summary: 'Private code-LLM with RAG over ~2B LOC; compile/test-aware hints and policy filters.',
      whatWeDid: 'Built a private code-LLM stack with retrieval-augmented code search across ~2B LOC, on-prem GPU cluster (NVIDIA), Triton/TensorRT-LLM serving, license/secret policy filters, secure IDE plugin, CWE/SAST-guided prompts.',
      feedback: 'Brought AI code help to our air-gapped environment; suggestions are safe and auditable.',
      stack: ['NVIDIA GPUs', 'Triton', 'TensorRT-LLM', 'RAG', 'SAST', 'IDE Plugins'],
      tags: ['On-Prem', 'Security', 'Code LLM', 'RAG', 'DevEx']
    },
    {
      id: 'p2',
      title: 'COBOL → Java Spring Boot modernization (Ford)',
      duration: '~9 months',
      category: 'Modernization',
      icon: 'Replace',
      summary: 'Static analysis + grammar transpilation + pattern libraries; containerized Spring services and parallel cutover.',
      whatWeDid: 'Pipeline combining static analysis, grammar-based transpilation, pattern libraries, and human-in-the-loop review. Auto-generated unit tests from execution traces; containerized Spring Boot services; parallel cutover with data reconciliation and performance gates.',
      feedback: 'Reduced migration risk and delivered maintainable Spring services with strong tests.',
      stack: ['COBOL', 'Java', 'Spring Boot', 'Docker', 'Kubernetes', 'JUnit'],
      tags: ['Legacy', 'Transpilation', 'Testing', 'Containers']
    },
    {
      id: 'p3',
      title: 'Automated seed identification & counting for vertical farm',
      duration: '~3 months',
      category: 'Computer Vision',
      icon: 'Scan',
      summary: 'Edge vision on Jetson for seed detection and counting; active learning loop; robot integration.',
      whatWeDid: 'Built a vision model for seed detection on trays; edge inference on Jetson; active-learning loop; API to integrate counts with planting robots and yield forecasting.',
      feedback: 'Counting is now instant and consistent; operators trust the numbers.',
      stack: ['Jetson', 'PyTorch', 'ONNX', 'FastAPI'],
      tags: ['Edge AI', 'Vision', 'Agritech']
    },
    {
      id: 'p4',
      title: 'Food & exercise tracking mobile app (on-device vision)',
      duration: '~5 months',
      category: 'Mobile AI',
      icon: 'Smartphone',
      summary: 'On-device plate segmentation and portion estimation; barcode/NDB integration; pose estimation for reps.',
      whatWeDid: 'On-device segmentation and portion estimation; barcode + NDB integration for macro/micro-nutrients; pose estimation for exercise form and rep counting; privacy-preserving health-data sync.',
      feedback: 'Frictionless logging boosted adherence; users love the no-typing flow.',
      stack: ['React Native', 'TensorFlow Lite', 'CoreML/MLKit', 'NDB APIs'],
      tags: ['On-Device', 'CV', 'Healthcare']
    },
    {
      id: 'p5',
      title: 'Digital Twin of the Kidney for catheter design',
      duration: '~8 months',
      category: 'Simulation/ML',
      icon: 'Droplets',
      summary: 'Hybrid CFD/biomech simulation with ML surrogates; UQ; engineer dashboard for geometry comparison.',
      whatWeDid: 'Hybrid CFD/biomechanical simulation calibrated with clinical data; ML surrogates to accelerate design exploration; uncertainty quantification; dashboard to compare catheter geometries under physiological scenarios.',
      feedback: 'Cut iteration time dramatically; engineers can test before prototyping.',
      stack: ['CFD', 'PyTorch', 'UQ', 'React'],
      tags: ['Digital Twin', 'Healthcare', 'Surrogate Models']
    },
    {
      id: 'p6',
      title: 'GenAI Sales Enablement with agentic flows',
      duration: '~4 months',
      category: 'RevOps',
      icon: 'Users',
      summary: 'Multi-agent system grounded on CRM/catalog/pricing; summaries; next-best-action; guardrails and logging.',
      whatWeDid: 'Multi-agent system grounded on CRM, product catalog, pricing rules, and service notes; call/email summarization; next-best-action and objection handling; guardrails for claims and compliance logging.',
      feedback: 'Reps onboard faster and spend more time selling.',
      stack: ['LLM', 'RAG', 'CRM APIs', 'Observability'],
      tags: ['Agentic', 'Sales', 'Governance']
    },
    {
      id: 'p7',
      title: 'Neurostack (open-source) — 8-layer agentic AI architecture',
      duration: 'ongoing',
      category: 'Open Source',
      icon: 'GitBranch',
      summary: '8-layer architecture: Infra, Agent Internet, Protocol, Tooling, Cognition, Memory, Application, Ops/Governance.',
      whatWeDid: 'Implemented an end-to-end architecture and reference implementation to bridge open-source solutions with enterprise needs across connectivity, protocols (MCP, A2A), tooling, cognition, memory, apps, and governance.',
      feedback: 'Used by leading companies to bridge open source and enterprise.',
      stack: ['MCP', 'A2A', 'Open Source', 'Observability'],
      tags: ['Framework', 'Agents', 'Enterprise']
    },
    {
      id: 'p8',
      title: 'Private Voice Assistant for regulated environments',
      duration: '~4 months',
      category: 'Voice AI',
      icon: 'Mic',
      summary: 'On-prem ASR/TTS + NLU with retrieval to policy docs; PII scrubbing; audit trails; offline option.',
      whatWeDid: 'On-prem ASR/TTS + NLU with retrieval to policy docs and KBs; hardened prompts; PII scrubbing and audits; integrates with ticketing and KM tools; full offline option.',
      feedback: 'A voice AI agent that enables talking to portals; managers love it.',
      stack: ['ASR', 'TTS', 'RAG', 'PII'],
      tags: ['On-Prem', 'Compliance', 'Voice']
    },
    {
      id: 'p9',
      title: 'Code Understanding RAG for polyglot monorepos',
      duration: '~10 weeks',
      category: 'Dev Productivity',
      icon: 'BookOpen',
      summary: 'Compiler-aware indexer (AST, CFG/DFG), dep graphs, symbol-respecting chunks; traceable answers; unit-test gen.',
      whatWeDid: 'Built compiler-aware indexer and dependency graphs with symbol-respecting chunking and "why" paths; unit-test generation from function specs.',
      feedback: 'Engineers ask repos questions and get precise, linked answers.',
      stack: ['AST', 'Graph', 'RAG', 'Testing'],
      tags: ['Monorepo', 'Indexing', 'Explainability']
    },
    {
      id: 'p10',
      title: 'DealFlow Co‑Pilot for Investment Bank (AI‑first CRM)',
      duration: '~12 weeks (MVP)',
      category: 'FinServe',
      icon: 'Briefcase',
      summary: 'Pitch-deck assembly, diligence checklists, call summaries, comps mining; RBAC, redaction, activity logs.',
      whatWeDid: 'Automated deck assembly from teasers/IMs, diligence checklist generation, earnings-call summarization, comparable set mining with strong RBAC, data redaction, and activity logs for governance.',
      feedback: 'Automates grunt work and keeps compliance happy.',
      stack: ['LLM', 'RBAC', 'Redaction', 'Audit'],
      tags: ['CRM', 'Banking', 'Compliance']
    }
  ];

  // Removed aggregate stats card due to insufficient global dataset

  // Mock data for industries
  const industries = [
    {
      id: 1,
      name: "Manufacturing",
      caseCount: 45,
      avgROI: "310%",
      description: "Transforming manufacturing through AI-powered automation, predictive maintenance, and quality control systems that optimize production efficiency and reduce operational costs.",
      keyMetrics: [
        { value: "40%", label: "Avg Efficiency Gain" },
        { value: "60%", label: "Defect Reduction" },
        { value: "35%", label: "Cost Savings" },
        { value: "99.2%", label: "Uptime" }
      ],
      useCases: ["Predictive Maintenance", "Quality Control", "Process Optimization", "Supply Chain", "Safety Monitoring"],
      featuredClients: [
        { name: "Ford", logo: "/assets/images/Ford-logo.jpg" },
        { name: "Siemens", logo: "/assets/images/Siemens-logo.png" }
      ]
    },
    {
      id: 2,
      name: "Financial Services",
      caseCount: 32,
      avgROI: "420%",
      description: "Securing financial operations with advanced fraud detection, risk assessment, and algorithmic trading solutions that protect assets while improving customer experience.",
      keyMetrics: [
        { value: "85%", label: "Fraud Detection" },
        { value: "70%", label: "False Positive Reduction" },
        { value: "$50M+", label: "Losses Prevented" },
        { value: "99.9%", label: "Uptime" }
      ],
      useCases: ["Fraud Detection", "Risk Assessment", "Algorithmic Trading", "Customer Analytics", "Compliance Monitoring"],
      featuredClients: [
        { name: "Citibank", logo: "/assets/images/Citibank-logo.png" }
      ]
    },
    {
      id: 3,
      name: "Healthcare",
      caseCount: 28,
      avgROI: "250%",
      description: "Improving patient outcomes through AI-powered diagnostic tools, clinical decision support, and healthcare analytics while maintaining strict HIPAA compliance.",
      keyMetrics: [
        { value: "35%", label: "Diagnosis Accuracy" },
        { value: "25%", label: "Treatment Time" },
        { value: "90%", label: "Adoption Rate" },
        { value: "100%", label: "HIPAA Compliance" }
      ],
      useCases: ["Clinical Decision Support", "Medical Imaging", "Drug Discovery", "Patient Monitoring", "Healthcare Analytics"],
      featuredClients: [
        { name: "Point32Health", logo: "/assets/images/Point32Health-logo.png" }
      ]
    },
    {
      id: 4,
      name: "Technology",
      caseCount: 38,
      avgROI: "340%",
      description: "Accelerating technology companies with MLOps platforms, AI infrastructure, and intelligent automation solutions that scale with business growth.",
      keyMetrics: [
        { value: "50%", label: "Development Speed" },
        { value: "99.9%", label: "System Reliability" },
        { value: "30%", label: "Cost Reduction" },
        { value: "Global", label: "Scale" }
      ],
      useCases: ["MLOps Platforms", "AI Infrastructure", "Automation", "Data Analytics", "Performance Optimization"],
      featuredClients: [
        { name: "Amazon", logo: "https://images.unsplash.com/photo-1523474253046-8cd2748b5fd2?w=50&h=50&fit=crop" },
        { name: "HCL", logo: "/assets/images/HCL-logo.png" },
        { name: "Goodleaf", logo: "/assets/images/GoodLeaf-logo.webp" },
        { name: "Norterra", logo: "/assets/images/Norterra-logo.png" },
        { name: "Google Cloud", logo: "https://images.unsplash.com/photo-1573804633927-bfcbcd909acd?w=50&h=50&fit=crop" }
      ]
    }
  ];

  // Filters removed for simplified page

  // Initialize filtered case studies
  useEffect(() => {
    let filtered = [...caseStudies];

    // Apply sorting only
    filtered?.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return b?.id - a?.id;
        case 'roi':
          return parseInt(b?.roi) - parseInt(a?.roi);
        case 'company':
          return a?.company?.localeCompare(b?.company);
        case 'industry':
          return a?.industry?.localeCompare(b?.industry);
        default:
          return 0;
      }
    });

    setFilteredCaseStudies(filtered);
  }, [sortBy]);

  // Filters removed

  const handleViewDetails = (caseStudy) => {
    setSelectedCaseStudy(caseStudy);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCaseStudy(null);
  };

  const handleViewProject = (project) => {
    setSelectedProject(project);
    setIsProjectModalOpen(true);
  };

  const handleCloseProjectModal = () => {
    setIsProjectModalOpen(false);
    setSelectedProject(null);
  };

  const getSimilarCases = (currentCase) => {
    return caseStudies?.filter(cs => cs?.id !== currentCase?.id && 
                   (cs?.industry === currentCase?.industry || 
                    cs?.solutionType === currentCase?.solutionType))?.slice(0, 3);
  };

  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'roi', label: 'Highest ROI' },
    { value: 'company', label: 'Company A-Z' },
    { value: 'industry', label: 'Industry' }
  ];

  return (
    <div className="min-h-screen bg-surface">
      
      {/* Hero Section */}
      <section className="pt-20 pb-12 bg-gradient-to-br from-primary to-steel-blue text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Client Success Stories
            </h1>
            <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
              Quantified results from our enterprise AI implementations across industries. 
              Discover how we've helped Fortune 500 companies achieve measurable transformation.
            </p>
            {/* Hero CTAs removed per request */}
          </div>
        </div>
      </section>
      {/* Stats Overview removed to keep the page strictly grounded in available case/project data */}
      {/* Industry Showcase */}
      <section className="pt-10 md:pt-14 pb-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <IndustryShowcase 
            industries={industries}
          />
        </div>
      </section>
      {/* Tab Switcher */}
      <section className="pt-6 pb-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-center">
            <div className="relative w-full max-w-2xl">
              <div className="absolute inset-0 -z-10 rounded-[28px] bg-gradient-to-r from-primary/20 to-indigo-500/20 blur-2xl" />
              <div className="relative rounded-[28px] border border-white/10 bg-white/5 p-1 backdrop-blur-md shadow-[0_8px_30px_rgba(37,99,235,0.25)]">
                <div
                  className="absolute top-1 left-1 bottom-1 w-1/2 rounded-[22px] bg-primary/25 border border-primary/40 shadow-[0_10px_30px_rgba(37,99,235,0.45)] transition-transform duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)]"
                  style={{ transform: activeTab === 'projects' ? 'translateX(100%)' : 'translateX(0%)' }}
                />
                <div className="relative grid grid-cols-2 gap-2">
                  <button
                    role="tab"
                    aria-selected={activeTab === 'companies'}
                    onClick={() => setActiveTab('companies')}
                    className={`relative z-10 flex items-center justify-center gap-2 rounded-[22px] px-6 py-4 text-lg md:text-xl font-semibold transition-colors ${
                      activeTab === 'companies' ? 'text-white' : 'text-white/80 hover:text-white'
                    }`}
                  >
                    <Icon name="Building2" size={20} />
                    Companies
                  </button>
                  <button
                    role="tab"
                    aria-selected={activeTab === 'projects'}
                    onClick={() => setActiveTab('projects')}
                    className={`relative z-10 flex items-center justify-center gap-2 rounded-[22px] px-6 py-4 text-lg md:text-xl font-semibold transition-colors ${
                      activeTab === 'projects' ? 'text-white' : 'text-white/80 hover:text-white'
                    }`}
                  >
                    <Icon name="FolderKanban" size={20} />
                    Projects
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col gap-8">
            {/* Main Content */}
            <div className="flex-1">
              {/* Controls */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
                <div>
                  {activeTab === 'companies' ? (
                    <>
                      <h2 className="text-2xl font-bold text-text-primary mb-2">
                        Companies & Outcomes
                      </h2>
                      <p className="text-text-secondary">
                        Engagements and quantified results across our partners
                      </p>
                    </>
                  ) : (
                    <>
                      <h2 className="text-2xl font-bold text-text-primary mb-2">
                        Project Portfolio
                      </h2>
                      <p className="text-text-secondary">
                        Selected projects with anonymized clients and measurable impact
                      </p>
                    </>
                  )}
                </div>

                <div className="flex items-center space-x-4" />
              </div>

              {/* Companies / Projects Grid */}
              {activeTab === 'companies' ? (
                filteredCaseStudies?.length > 0 ? (
                  <div className="grid gap-6 grid-cols-1 xl:grid-cols-2">
                    {filteredCaseStudies?.map((caseStudy) => (
                      <CaseStudyCard
                        key={caseStudy?.id}
                        caseStudy={caseStudy}
                        onViewDetails={handleViewDetails}
                      />
                    ))}
                  </div>
                ) : null
              ) : (
                <div className="grid gap-6 grid-cols-1 xl:grid-cols-2">
                  {projects?.map((project) => (
                    <ProjectCard key={project?.id} project={project} />
                  ))}
                </div>
              )}

              {/* Similar Challenges Section */}
              {activeTab === 'companies' && selectedCaseStudy && (
                <SimilarChallengesSection
                  currentCaseStudy={selectedCaseStudy}
                  similarCases={getSimilarCases(selectedCaseStudy)}
                  onViewCase={handleViewDetails}
                />
              )}
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-primary text-white">
        <div className="max-w-4xl mx-auto text-center px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Create Your Success Story?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join industry leaders who have transformed their operations with our AI solutions. 
            Let's discuss how we can help you achieve similar results.
          </p>
          {/* Bottom CTAs removed per request */}
        </div>
      </section>
      {/* Modals */}
      <CaseStudyModal
        caseStudy={selectedCaseStudy}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
      <ProjectModal
        project={selectedProject}
        isOpen={isProjectModalOpen}
        onClose={handleCloseProjectModal}
      />
      {/* Footer removed because a global footer is now injected site-wide */}
    </div>
  );
};

export default ClientSuccessStoriesPage;